import { KpiSummaryViewEnum } from '../../shared/services/data-services/graphql';
import moment from 'moment';
import * as momentTZ from 'moment-timezone';
import { ITimesTemp } from '@wind-log_library/fe-component-library';

export function checkKpiSummaryView(index: number): KpiSummaryViewEnum {
  return [
    KpiSummaryViewEnum.Standard,
    KpiSummaryViewEnum.Availability,
    KpiSummaryViewEnum.Reliability,
    KpiSummaryViewEnum.Custom,
  ][index];
}

export function getChartDropdownOptions(currency: string): any[] {
  return [
    {
      displayName: 'KPI.PORTFOLIO.PROCESSES_LIST_TABLE.DURATION_PLANNED',
      objectKey: 'durationPlanned',
      isDuration: true
    },
    {
      displayName: 'KPI.PORTFOLIO.PROCESSES_LIST_TABLE.DURATION_REALIZED',
      objectKey: 'durationRealized',
      isDuration: true
    },
    {
      displayName: 'KPI.PORTFOLIO.PROCESSES_LIST_TABLE.PROD_LOSS_REALIZED',
      objectKey: 'productionLossRealized',
      objectSign: 'kWh'
    },
    {
      displayName: 'KPI.PORTFOLIO.PROCESSES_LIST_TABLE.REV_LOSS_REALIZED',
      objectKey: 'revenueLossRealized',
      objectSign: currency
    }
  ];
}

export function getProcessClass(value: string): string {
  switch (value) {
    case 'Environmental':
      return 'category-environmental';
    case 'Grid Limitation':
      return 'category-grid-limitation';
    case 'Maintenance':
      return 'category-maintenance';
    case 'Repairs':
      return 'category-repairs';
    case 'Inspection':
      return 'category-inspection';
    default:
      return '';
  }
}

export function randomColor(
  ranges: (number[])[] = [[0, 60], [180, 359]],  // allowed colors in H
  sProp: number[] = [30, 43], //  S property shade,
  lProp: number[] = [38, 47] //  L property shade
): string {
  // get max random
  let total = 0;
  let i;
  for (i = 0; i < ranges.length; i += 1) {
    total += ranges[i][1] - ranges[i][0] + 1;
  }

  // get random hue index
  let randomHue = Math.floor(Math.random() * total);

  // convert index to actual hue
  let pos = 0;
  for (i = 0; i < ranges.length; i += 1) {
    pos = ranges[i][0];
    if (randomHue + pos <= ranges[i][1]) {
      randomHue += pos;
      break;
    } else {
      randomHue -= (ranges[i][1] - ranges[i][0] + 1);
    }
  }

  const randomIntFromInterval = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1) + min);
  };

  return `hsl(${randomHue}, ${randomIntFromInterval(sProp[0], sProp[1])}%, ${randomIntFromInterval(lProp[0], lProp[1])}%)`;
}

export function timeRangeLessThanOneMonth(start: number, end: number): boolean {
  return moment(end).diff(moment(start), 'days') <= 31;
}

export function roundToHigherNumber(num: number, target: number = 10): number {
  let result = target;
  if (!!num) {
    if (num > Number.MAX_SAFE_INTEGER) {
      return num;
    }
    result = Math.floor(num);
    while (result % target !== 0) {
      result += 1;
    }
  }
  return result;
}

export function roundToLoverNumber(num: number, target: number = 10): number {
  let result = target;
  if (!!num) {
    if (num < Number.MIN_SAFE_INTEGER) {
      return num;
    }
    result = Math.floor(num);
    while (result % target !== 0) {
      result -= 1;
    }
  }
  return result;
}

export function adjustTimeZone(timesTemp: ITimesTemp, timeZone: string = 'Europe/Berlin'): ITimesTemp {
  const yearStart = moment(timesTemp.startTS).year();
  const monthStart = moment(timesTemp.startTS).month();
  const dayStart = moment(timesTemp.startTS).date();

  const yearEnd = moment(timesTemp.endTS).year();
  const monthEnd = moment(timesTemp.endTS).month();
  const dayEnd = moment(timesTemp.endTS).date();

  const startTSAdjusted = momentTZ.tz(0, timeZone).year(yearStart).month(monthStart).date(dayStart).startOf('day').valueOf();
  const endTSAdjusted = momentTZ.tz(0, timeZone).year(yearEnd).month(monthEnd).date(dayEnd).endOf('day').valueOf();

  return {
    startTS: startTSAdjusted,
    endTS: endTSAdjusted + 1 // We add +1 milisecond to get the exact start of next day for the selected period
  };
}

export function adjustSingleDateTimezone(timestamp: number): number {
  const userTimezone = momentTZ.tz.guess();
  const year = moment(timestamp).year();
  const month = moment(timestamp).month();
  const day = moment(timestamp).date();
  return momentTZ.tz(0, userTimezone).year(year).month(month).date(day).endOf('day').valueOf() + 1;
}

export function kpiSummaryCompare(a: any, b: any, isAsc: boolean, type: number): number {
  return type === 1 ? a < b ? -1 : 1 * (isAsc ? 1 : -1) : kpiSummaryCompareNames(a, b, isAsc);
}

export function kpiSummaryCompareNames(a: any, b: any, isAsc: boolean): number {
  return isAsc ? a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'})
    : b.localeCompare(a, undefined, {numeric: true, sensitivity: 'base'});
}

export function copyObject(obj: any): any {
  return JSON.parse(JSON.stringify(obj));
}

export function getUniqueCustomAvailabilities(assets: any[]): any[] {
  const availabilities = (assets ?? []).flatMap(asset => asset?.availabilities ?? []).filter(av => !av.isMain);
  return availabilities.filter((av, index) => availabilities.findIndex(other => other.name === av.name) === index);
}

export function setKpiSummaryMonthTableSubName(current: number, startTS: number, endTS: number): string {
  const currentMonthStart = moment(current).startOf('month').valueOf();
  const currentMonthEnd = moment(current).endOf('month').valueOf();
  const end = moment(endTS).endOf('day').valueOf() + 1;
  const isStartTsCurrent = moment(current).startOf('month').valueOf() === moment(startTS).startOf('month').valueOf();
  const isEndTsCurrent = moment(current).startOf('month').valueOf() === moment(endTS).startOf('month').valueOf();
  if (currentMonthStart < startTS || end < currentMonthEnd + 1) {
    return `(${moment(isStartTsCurrent ? startTS : moment(current).startOf('month').valueOf()).format('DD')}-${moment(isEndTsCurrent ? endTS : currentMonthEnd).format('DD')})`;
  }
  return '';
}
